import { LineCopy } from '@qr-front/icons/line-copy'
import { Button } from '@qr-front/ui/components/Button'
import {
	Dialog,
	DialogDescription,
	DialogTitle,
} from '@qr-front/ui/components/Dialog'
import { DialogContent } from '@qr-front/ui/components/Dialog'
import { Input } from '@qr-front/ui/components/Input'
import { cn } from '@qr-front/ui/utils'
import { FocusRemover } from 'components/FocusRemover'
import { showNotification } from 'components/Notification'
import { useAtom } from 'jotai'
import { useTranslations } from 'next-intl'
import { useCallback, useRef, useState } from 'react'
import { buildUrl } from 'utils/urlUtils'

import { copyToClipboard } from '@/utils'

import { shareQrDialogAtom, shareQrLinkAtom } from '../dialogsState'

export const ShareQrDialog = () => {
	const t = useTranslations()
	const [isCopied, setIsCopied] = useState(false)
	const [shareQrOpen, setShareQrOpen] = useAtom(shareQrDialogAtom)
	const [shareQrLink, setShareQrLink] = useAtom(shareQrLinkAtom)

	const urlRef = useRef<HTMLInputElement>(null)

	const url = buildUrl(`qr-download/${shareQrLink}`)

	const onCopy = useCallback(async () => {
		if (!url) return

		if (!(await copyToClipboard(url))) {
			urlRef.current?.focus()
			urlRef.current?.select()
			showNotification(t('public__generic__unexpected_error'), {
				type: 'error',
			})
			return
		}

		setIsCopied(true)
		setTimeout(() => {
			setIsCopied(false)
		}, 3000)
	}, [url, t])

	const onOpenChange = useCallback(
		(open: boolean) => {
			setShareQrOpen(open)
			if (!open) {
				setShareQrLink('')
				setIsCopied(false)
			}
		},
		[setShareQrLink, setShareQrOpen],
	)

	return (
		<Dialog open={shareQrOpen} onOpenChange={onOpenChange}>
			<DialogContent
				closeButtonLabel={t('ui__popup__close_button')}
				className="w-full p-0 tablet:w-[500px]"
			>
				<FocusRemover />
				<div className="flex flex-col gap-4 p-6 tablet:gap-6 tablet:p-8">
					<div className="flex flex-col gap-2">
						<DialogTitle>
							{t('dashboard__share__qr_code__modal_title')}
						</DialogTitle>
						<DialogDescription className="text-dark-grey">
							{t('dashboard__share__qr_code__modal_description')}
						</DialogDescription>
					</div>
					<div className="flex flex-col">
						<div className="flex gap-4 tablet:gap-2">
							<Input
								ref={urlRef}
								containerClassName="w-full"
								className="h-10 placeholder:type-body-small"
								value={url}
								readOnly
							/>
							<Button className="px-4 py-3" onClick={onCopy}>
								<LineCopy className="size-4" />
								{t('dashboard__share__qr_code__modal_copy_button')}
							</Button>
						</div>
						<span
							className={cn(
								'type-body-medium -mb-6 flex w-full text-center text-green opacity-0 transition-opacity duration-300 ease-in-out word-break-fix',
								isCopied && 'opacity-100',
							)}
						>
							{t('generator__share_qr_dialog__success_copy_button')}
						</span>
					</div>
				</div>
			</DialogContent>
		</Dialog>
	)
}
